import Decimal from 'decimal.js-light';

// 计算组合
export function combine(oddsArr: any[], k: number): number[][] {
  const result: number[][] = [];
  function dfs(start: number, path: number[]) {
    if (path.length === k) {
      result.push(path);
      return;
    }
    for (let i = start; i < oddsArr.length; i++) {
      dfs(i + 1, [...path, i]);
    }
  }
  dfs(0, []);
  return result;
}

function binomial(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let res = 1;
  for (let i = 0; i < k; ++i) {
    res *= n - i;
    res /= i + 1;
  }
  return Math.round(res);
}

/**
 * 根据玩法类型直接返回标准总注数（与后端 betsCount 完全一致）
 * @param type 玩法类型，如 "2/3", "Trixie", "Lucky 15", "Goliath"
 * @returns 总注数
 */
export function getBetCount(type: string): number {
  if (type.includes('/')) {
    const [up, down] = type.split('/').map(Number);
    return binomial(down, up); // C(down, up)
  }

  const betCountMap: Record<string, number> = {
    Trixie: 4,
    Patent: 7,
    Yankee: 11,
    'Lucky 15': 15,
    Canadian: 26,
    'Lucky 31': 31,
    Heinz: 57,
    'Lucky 63': 63,
    'Super Heinz': 120,
    Goliath: 247,
  };

  if (betCountMap[type]) {
    return betCountMap[type];
  }

  throw new Error(`不支持的玩法类型: ${type}`);
}

/**
 * 计算潜在收益
 * @param oddsArr 投注数组，每项至少包含 odds
 * @param type    玩法类型，后端 value 值，如 "2/3", "Trixie", "Lucky 15", "Goliath"
 * @param stake   每注金额，默认 10
 */
export function calcPotentials(oddsArr: any[], type: string, stake: number = 10): Decimal {
  if (oddsArr.length === 0) return new Decimal(0);

  const odds = oddsArr.map((o) => new Decimal(o.odds));
  const n = odds.length;
  const stakeDec = new Decimal(stake);

  let total = new Decimal(0);

  // Step 1: 处理简单类型，如 "2/3", "3/5", "4/6" 等
  if (type.includes('/')) {
    const [upStr, downStr] = type.split('/');
    const k = Number(upStr); // systemSize
    const requiredEvents = Number(downStr);

    if (n !== requiredEvents) {
      console.warn(`场次不匹配：玩法 ${type} 需要 ${requiredEvents} 场，实际提供 ${n} 场`);
    }

    if (k < 1 || k > n) return total;

    const combos = generateCombinations(n, k);
    for (const combo of combos) {
      const prod = combo.reduce((p, idx) => p.mul(odds[idx]), new Decimal(1));
      total = total.plus(prod.mul(stakeDec));
    }

    return total;
  }

  // Step 2: 处理复合命名玩法（不包含 '/'）
  const sizes: number[] = [];
  let includeSingles = false;

  switch (type) {
    case 'Trixie':
      sizes.push(2, 3);
      break;
    case 'Patent':
      includeSingles = true;
      sizes.push(1, 2, 3);
      break;
    case 'Yankee':
      sizes.push(2, 3, 4);
      break;
    case 'Lucky 15':
      includeSingles = true;
      sizes.push(1, 2, 3, 4);
      break;
    case 'Canadian':
      sizes.push(2, 3, 4, 5);
      break;
    case 'Lucky 31':
      includeSingles = true;
      sizes.push(1, 2, 3, 4, 5);
      break;
    case 'Heinz':
      sizes.push(2, 3, 4, 5, 6);
      break;
    case 'Lucky 63':
      includeSingles = true;
      sizes.push(1, 2, 3, 4, 5, 6);
      break;
    case 'Super Heinz':
      sizes.push(2, 3, 4, 5, 6, 7);
      break;
    case 'Goliath':
      sizes.push(2, 3, 4, 5, 6, 7, 8);
      break;
    default:
      throw new Error(`Unsupported system type: ${type}`);
  }

  // 验证场次是否匹配（可选，防止误用）
  const expectedEvents = sizes.length === 0 ? 0 : Math.max(...sizes);
  if (includeSingles) {
    // Lucky/Patent 系列场次等于最大串数
    if (n !== expectedEvents) {
      console.warn(`场次建议：玩法 ${type} 通常用于 ${expectedEvents} 场赛事，当前提供 ${n} 场`);
    }
  } else {
    // 如 Yankee 用在 4 场，Trixie 用在 3 场
    if (n !== expectedEvents && n !== sizes[sizes.length - 1]) {
      console.warn(`场次建议：玩法 ${type} 通常用于 ${expectedEvents} 场赛事，当前提供 ${n} 场`);
    }
  }

  for (const k of sizes) {
    if (k === 1) {
      // 单关
      for (let i = 0; i < n; i++) {
        total = total.plus(odds[i].mul(stakeDec));
      }
    } else if (k <= n) {
      const combos = generateCombinations(n, k);
      for (const combo of combos) {
        const prod = combo.reduce((p, idx) => p.mul(odds[idx]), new Decimal(1));
        total = total.plus(prod.mul(stakeDec));
      }
    }
  }

  return total;
}

/**
 * 生成所有 k 大小的组合索引（返回 number[][]）
 */
export function generateCombinations(n: number, k: number): number[][] {
  const result: number[][] = [];
  const path: number[] = [];

  function dfs(start: number) {
    if (path.length === k) {
      result.push([...path]);
      return;
    }
    for (let i = start; i < n; i++) {
      path.push(i);
      dfs(i + 1);
      path.pop();
    }
  }

  dfs(0);
  return result;
}
